import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { adminApi } from '../../api/client';
import { formatCurrency } from '../../lib/currency';

const STATUS_LABELS = {
  PENDING_PAYMENT: 'Wacht op betaling', confirmed: 'Bevestigd', processing: 'In verwerking',
  shipped: 'Verzonden', delivered: 'Geleverd', cancelled: 'Geannuleerd', refunded: 'Terugbetaald',
};
const STATUS_COLORS = {
  PENDING_PAYMENT: 'bg-yellow-100 text-yellow-800', confirmed: 'bg-blue-100 text-blue-800',
  processing: 'bg-blue-100 text-blue-800', shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800',
  refunded: 'bg-gray-100 text-gray-700',
};
const VALID_TRANSITIONS = {
  PENDING_PAYMENT: ['confirmed', 'cancelled'],
  confirmed: ['processing', 'cancelled', 'refunded'],
  processing: ['shipped', 'cancelled'],
  shipped: ['delivered'],
};

function Row({ label, children }) {
  return (<div className="flex gap-3"><span className="text-muted-foreground w-36 shrink-0 font-medium text-sm">{label}</span><span className="text-sm text-foreground">{children}</span></div>);
}

export default function AdminOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    adminApi.getOrder(id).then(r => setOrder(r.data)).finally(() => setLoading(false));
  }, [id]);

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    await adminApi.updateOrderStatus(id, newStatus);
    setOrder(o => ({ ...o, status: newStatus }));
    setUpdating(false);
  };

  if (loading) return <div className="p-8"><div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" /></div>;
  if (!order) return <div className="p-8 text-muted-foreground">Bestelling niet gevonden.</div>;

  const transitions = VALID_TRANSITIONS[order.status] || [];
  const addr = order.deliveryAddressSnapshot || {};
  const cust = order.customerAddressSnapshot;
  const bill = order.billingAddressSnapshot;
  const opt = order.deliveryOptionSnapshot;
  const houseNum = addr.houseNumber ?? addr.number ?? '';
  const country = addr.countryCode ?? addr.country ?? '';
  const isStructuredCountry = ['BE', 'NL', 'DE'].includes((country || '').toUpperCase());
  const missingHouseNumber = isStructuredCountry && !houseNum;
  const renderAddr = (a = {}) => {
    const hn = a.houseNumber ?? a.number ?? '';
    const cc = a.countryCode ?? a.country ?? '';
    return [
      `${a.street || '--'}${hn ? ' ' + hn : ''}${a.bus ? ' bus ' + a.bus : ''}`,
      `${a.postalCode || ''} ${a.city || ''}`.trim(),
      cc,
    ].filter(Boolean).join(', ');
  };

  return (
    <div data-testid="admin-order-detail" className="p-8 max-w-3xl">
      <Link to="/admin/orders" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6 font-medium">
        <ArrowLeft className="w-4 h-4" /> Bestellingen
      </Link>

      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl font-light text-foreground">#{order.orderNumber}</h1>
          <p className="text-sm text-muted-foreground mt-1">{order.created_at ? new Date(order.created_at).toLocaleDateString('nl-BE', { dateStyle: 'long' }) : ''}</p>
        </div>
        <span data-testid="order-status" className={`px-4 py-1.5 rounded-full text-sm font-semibold ${STATUS_COLORS[order.status] || 'bg-muted text-muted-foreground'}`}>
          {STATUS_LABELS[order.status] || order.status}
        </span>
      </div>

      {/* Order info */}
      <div className="bg-card border border-border rounded-2xl p-6 mb-6 space-y-3 shadow-sm">
        <Row label="Klant">{order.user?.name || order.user?.email || order.guestEmail || '--'}</Row>
        <Row label="Totaal">{formatCurrency(order.totalAmount, 'nl')}</Row>
        {order.discountType && <Row label="Korting">{order.discountType} ({formatCurrency(order.discountAmount, 'nl')} excl. BTW)</Row>}
        {order.stackDiscountAmount > 0 && <Row label="Stapelkorting">{formatCurrency(order.stackDiscountAmount, 'nl')} excl. BTW</Row>}
        <Row label="BTW verlegd">{order.vatReversed ? 'Ja (Art. 39bis)' : 'Nee'}</Row>
        {!order.vatReversed && <Row label="BTW bedrag">{formatCurrency(order.vatAmount, 'nl')}</Row>}
        <Row label="Verzendmethode">
          <span data-testid="order-delivery-method" className="inline-flex items-center gap-2">
            {order.deliveryMethod || 'HOME'}
            {order.carrier && <span className="text-muted-foreground">via {order.carrier}</span>}
          </span>
        </Row>
        {order.molliePaymentId && <Row label="Betaling">{order.molliePaymentId} ({order.payment?.status || '--'})</Row>}
        {order.prescriberSnapshot && (
          <Row label="Voorschrijver">
            <span data-testid="order-prescriber" className="flex flex-col leading-tight">
              <span>
                {`${order.prescriberSnapshot.firstName || ''} ${order.prescriberSnapshot.lastName || ''}`.trim() || '--'}
                {order.prescriberSnapshot.profession ? ` · ${order.prescriberSnapshot.profession}` : ''}
              </span>
              {order.prescriberSnapshot.orgName && (
                <span className="text-xs text-muted-foreground mt-0.5">{order.prescriberSnapshot.orgName}</span>
              )}
            </span>
          </Row>
        )}
        {cust && <Row label="Klantadres" data-testid="order-customer-address">{renderAddr(cust)}</Row>}
        {bill && <Row label="Facturatieadres" data-testid="order-billing-address">{renderAddr(bill)}</Row>}
        {addr && <Row label="Leveringsadres" data-testid="order-delivery-address">{renderAddr(addr)}</Row>}
        {missingHouseNumber && (
          <div data-testid="missing-house-number-warning" className="text-xs text-destructive bg-destructive/10 border border-destructive/20 px-3 py-2 rounded-lg">
            Geen huisnummer ingevuld voor {country}. Active Ants kan deze bestelling weigeren — vul het huisnummer aan voordat u de order pusht.
          </div>
        )}
        {opt && opt.method && opt.method !== 'HOME' && (
          <div className="border-t border-border pt-3 mt-3 space-y-1" data-testid="order-pickup-point">
            <p className="text-sm font-semibold text-foreground">Afhaalpunt</p>
            <p className="text-sm text-muted-foreground">
              {opt.pointName || '--'} <span className="text-xs">({opt.pointType || '--'} · {opt.pointId || '--'})</span>
            </p>
            {opt.address && <p className="text-sm text-foreground">{renderAddr(opt.address)}</p>}
          </div>
        )}
        {order.notes && <Row label="Opmerkingen">{order.notes}</Row>}
      </div>

      {/* Order lines */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden mb-6 shadow-sm">
        <div className="px-6 py-4 border-b border-border bg-muted/50">
          <h2 className="font-serif text-lg font-light text-foreground">Orderregels</h2>
        </div>
        {order.lines?.length > 0 ? (
          <div className="divide-y divide-border">
            {order.lines.map(line => (
              <div key={line._id} className="flex items-center justify-between px-6 py-4 text-sm">
                <div>
                  <span className="text-foreground">{line.nameSnapshot_nl} <span className="text-muted-foreground">x {line.quantity}</span></span>
                  {line.freeUnits > 0 && <span className="ml-2 text-xs text-green-700">({line.freeUnits} gratis)</span>}
                </div>
                <span className="font-semibold">{formatCurrency(line.effectiveUnitPrice ? line.effectiveUnitPrice * line.quantity : line.priceSnapshot * line.quantity, 'nl')}</span>
              </div>
            ))}
          </div>
        ) : <p className="px-6 py-4 text-sm text-muted-foreground">Geen orderregels</p>}
      </div>

      {/* Status transitions */}
      {transitions.length > 0 && (
        <div className="flex gap-3">
          {transitions.map(s => (
            <button key={s} data-testid={`status-${s}`} onClick={() => updateStatus(s)} disabled={updating}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all active:scale-95 disabled:opacity-50 ${
                s === 'cancelled' || s === 'refunded' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm'
              }`}>
              {STATUS_LABELS[s] || s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
